import { type Dispatch, type SetStateAction, useEffect } from 'react'

import { formatDate } from 'commons/utils/date'
import { numberToDecimalString } from 'commons/utils/number'
import { type HeadItemType, LoadingHavaianas, Pagination, Table } from 'components'
import {
  ColoredText,
  LoadingWrapper,
  ValueDivider,
  ValueRow,
} from 'container/BackofficeRanges/styles'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import {
  getTracking,
  resetTrackingPagination,
  setTrackingPage,
} from 'store/BackofficeContext/backofficeSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

interface TrackingModalProps {
  setOpen: Dispatch<SetStateAction<boolean>>
}

const TrackingModal = ({ setOpen }: TrackingModalProps) => {
  const dispatch = useDispatch()
  const {
    loadingTracking,
    trackingData,
    trackingPag: { totalPages, totalItems, currentPage, pageSize },
  } = useSelector((state) => state.backoffice)

  const rangeStatus = {
    analysis: 30,
    approved: 100,
    pending: 20,
    rejected: 90,
  }

  const getStatusColor = (status: number) => {
    if (status === rangeStatus.approved) return theme.colors.alert.success[40]
    if ([rangeStatus.pending, rangeStatus.analysis].includes(status)) {
      return theme.colors.alert.warning[40]
    }
    if (status === rangeStatus.rejected) return theme.colors.alert.danger[40]
    return theme.colors.neutral.high
  }

  const tableLabels: HeadItemType[] = [
    { columnName: 'Data', label: 'Data' },
    { columnName: 'Hora', label: 'Hora' },
    { columnName: 'Usuario', label: 'Usuário' },
    { columnName: 'Status', label: 'Status' },
    { columnName: 'Regional', label: 'Regional' },
    { columnName: 'Alteracao', label: 'Alteração' },
    { columnName: 'Justificativa', label: 'Justificativa' },
  ]

  const toDec = numberToDecimalString

  const tableData = () => {
    if (!trackingData.length) return []

    return trackingData.map((item) => {
      const renderChange = () => {
        if (item.PersentualSugerido !== item.PersentualVigente) {
          const percent = `${toDec(item.PersentualVigente)}% ->
          ${toDec(item.PersentualSugerido)}%`
          if (item.ValorSugerido !== item.ValorVigente) {
            const currency = `R$ ${toDec(item.ValorVigente)} ->
            R$ ${toDec(item.ValorSugerido)}`
            return (
              <ColoredText color={getStatusColor(item.Status.Codigo)}>
                <ValueRow>
                  {percent}
                  <ValueDivider />
                  {currency}
                </ValueRow>
              </ColoredText>
            )
          }
          return <ColoredText color={getStatusColor(item.Status.Codigo)}>{percent}</ColoredText>
        } else if (item.ValorSugerido !== item.ValorVigente) {
          return (
            <ColoredText color={getStatusColor(item.Status.Codigo)}>
              {`R$ ${toDec(item.ValorVigente)} -> R$ ${toDec(item.ValorSugerido)}`}
            </ColoredText>
          )
        }
        return 'Nehuma alteração'
      }

      return {
        content: [
          formatDate(item.DataCadastro, 'DD/MM/YYYY'),
          formatDate(item.DataCadastro, 'HH:mm'),
          item.UsuarioCadastro,
          <ColoredText
            color={getStatusColor(item.Status.Codigo)}
            key={`status-${item.DataCadastro}`}
          >
            {item.Status.Descricao}
          </ColoredText>,
          `${item.CodigoEquipe} - ${item.NomeEquipe}`,
          renderChange(),
          item.Observacao ?? '',
        ],
        id: item.DataCadastro,
      }
    })
  }

  const handleClose = () => {
    dispatch(resetTrackingPagination())
    setOpen(false)
  }

  const handlePageChange = (page: number) => {
    dispatch(setTrackingPage(page))
    dispatch(getTracking(page, pageSize))
  }

  useEffect(() => {
    dispatch(getTracking(1, pageSize))
  }, [])

  return (
    <ModalContentLayout
      closeAction={handleClose}
      closeButton
      description="Acompanhe todas as atualizações realizadas neste pedido."
      title="Tracking"
      titleSize={22}
      width={'95vw'}
    >
      {loadingTracking ? (
        <LoadingWrapper>
          <LoadingHavaianas />
        </LoadingWrapper>
      ) : (
        <Table bodyData={tableData()} fixedHead headData={tableLabels} rowHeight={40} />
      )}
      <div>
        <Pagination
          currentPage={currentPage}
          disabled={loadingTracking}
          itemsPerPage={pageSize}
          numberOfItems={totalItems}
          numberOfPages={totalPages}
          setCurrentPage={handlePageChange}
        />
      </div>
    </ModalContentLayout>
  )
}

export default TrackingModal
